import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronRight, Loader2, Search } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { EmptyCard, ErrorCard, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { useAdminSession } from "../components/jamora-admin/SessionProvider";
import { adminError, isSessionRequired, pharmacyPath } from "../lib/jamoraAdmin";

// Home of someone who may open a pharmacy's file but not read the platform's registry: names and states only (the server reduces the list).
export default function JamoraAdminPharmacyChoice() {
  const session = useAdminSession();
  const [search, setSearch] = useState("");
  const [state, setState] = useState({ phase: "loading", items: [], cursor: null, error: "" });
  const seq = useRef(0);
  const load = useCallback(async (term, cursor) => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: cursor ? "more" : "loading", error: "", items: cursor ? s.items : [] }));
    try {
      const { data } = await api.get("/jamora-admin/pharmacy-options", { params: { limit: 50, ...(term ? { search: term } : {}), ...(cursor ? { cursor } : {}) } });
      if (mine !== seq.current) return;
      setState((s) => ({ phase: "ready", items: cursor ? [...s.items, ...data.items] : data.items, cursor: data.next_cursor, error: "" }));
      session?.touch();
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { session?.expire(); return; }
      setState((s) => ({ ...s, phase: "error", error: adminError(e) }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const timer = setTimeout(() => load(search.trim(), null), search ? 300 : 0);
    return () => { clearTimeout(timer); seq.current += 1; };
  }, [search, load]);

  return (
    <div className="space-y-4" data-testid="admin-choice">
      <PageHeader title="Choisir une pharmacie" description="Ouvrez la fiche de la pharmacie à consulter. Tout est en lecture seule." />
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom de la pharmacie…" className="pl-9 h-10" aria-label="Chercher une pharmacie" data-testid="choice-search" />
      </div>
      {state.phase === "loading" && <LoadingBlock rows={3} />}
      {state.phase === "error" && <ErrorCard message={state.error} onRetry={() => load(search.trim(), null)} />}
      {state.phase === "ready" && !state.items.length && <EmptyCard icon={Building2} title="Aucune pharmacie ne correspond" testid="choice-empty" />}
      {(state.phase === "ready" || state.phase === "more") && state.items.length > 0 && (
        <Card className="divide-y divide-slate-100" data-testid="choice-list">
          {state.items.map((p) => (
            <Link key={p.schema} to={pharmacyPath(p.schema)} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`choice-${p.schema}`}>
              <Building2 size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
              <span className="flex-1 min-w-0 truncate font-medium text-slate-900">{p.name}</span>
              {!p.active && <Pill tone="red">Désactivée</Pill>}{p.demo && <Pill tone="violet">Démo</Pill>}
              <ChevronRight size={16} className="text-slate-300 shrink-0" aria-hidden="true" />
            </Link>
          ))}
        </Card>
      )}
      {state.cursor && <Button variant="outline" size="sm" onClick={() => load(search.trim(), state.cursor)} disabled={state.phase === "more"}>{state.phase === "more" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Voir plus</Button>}
    </div>
  );
}
